import { cyfDefaultBrandingColors, cyfDefaultFonts, wlDefaultFonts, deriveThemeTokens } from '../../constants/cyfDefaultBranding';

const WL_FONT_LINK_ID = 'wl-google-fonts';
const WL_FONT_STYLE_ID = 'wl-custom-font-css';

export const getContrast = (backgroundColor) => {
  if (typeof backgroundColor !== 'string' || !backgroundColor.startsWith('#')) return '#000000';
  const [r, g, b] = [0, 2, 4].map((i) => parseInt(backgroundColor.replace('#', '').slice(i, i + 2), 16));
  return (r * 299 + g * 587 + b * 114) / 1000 > 125 ? '#000000' : '#FFFFFF';
};

export const getActualColor = (colorVar) => {
  if (!colorVar) return '';
  if (colorVar.startsWith('var(--')) {
    const varName = colorVar.match(/var\((.*?)\)/)[1];
    return getComputedStyle(document.documentElement).getPropertyValue(varName).trim() || '';
  }
  return colorVar;
};

const setVar = (name, value) => {
  if (value != null) document.documentElement.style.setProperty(name, value);
};

const applyColorsToCSSVars = (colors) => {
  if (!colors || !Object.keys(colors).length) return;

  const derived = deriveThemeTokens(colors);

  setVar('--primary', colors.primaryColor || colors.primaryButton);
  setVar('--primary-foreground', getContrast(colors.primaryColor || colors.primaryButton));
  setVar('--color-primary', colors.primaryColor || colors.primaryButton);
  setVar('--main-logo-background', colors.mainLogoBackground || colors.sidebarBackground);

  setVar('--secondary', colors.secondaryColor || colors.primaryButton);
  setVar('--secondary-foreground', colors.btnTextColor || getContrast(colors.secondaryColor || colors.primaryButton));
  setVar('--color-secondary', colors.secondaryColor);

  setVar('--accent', colors.secondaryColor);
  setVar('--accent-foreground', getContrast(colors.secondaryColor));

  setVar('--background', colors.bgColor || '#F5F0E8');
  setVar('--foreground', colors.textColor || '#1C2B26');
  setVar('--color-background', colors.bgColor || '#F5F0E8');

  setVar('--sidebar-background', colors.sidebarBackground);
  setVar('--sidebar-foreground', getContrast(colors.sidebarBackground));
  setVar('--sidebar-background-hover', colors.sidebarLinkBackgroundHover || derived.buttonHover);
  setVar('--sidebar-link-color', colors.sidebarLinkColor);
  setVar('--sidebar-link-hover', colors.sidebarLinkHover);
  setVar('--sidebar-link-focus', colors.sidebarLinkFocus);
  setVar('--sidebar-primary', colors.sidebarLinkFocus || colors.primaryColor);
  setVar('--sidebar-primary-foreground', getContrast(colors.sidebarLinkFocus || colors.primaryColor));
  setVar('--sidebar-accent', derived.sidebarAccent);
  setVar('--sidebar-accent-foreground', derived.sidebarAccentForeground);
  setVar('--sidebar-border', derived.sidebarBorder);
  setVar('--sidebar', colors.sidebarBackground);
  setVar('--color-sidebar', colors.sidebarBackground);

  setVar('--card', colors.cardBackground || '#FDFCF9');
  setVar('--card-foreground', colors.textColor || '#1C2B26');
  setVar('--color-card-background', colors.cardBackground || '#FDFCF9');

  setVar('--header-background', colors.headerBackground || '#FDFCF9');
  setVar('--header-foreground', colors.textColor || '#1C2B26');
  setVar('--color-header-background', colors.headerBackground || '#FDFCF9');

  setVar('--popover', derived.popoverBackground);
  setVar('--popover-foreground', derived.popoverForeground);

  setVar('--muted', derived.mutedColor);
  setVar('--muted-foreground', derived.mutedForeground);

  setVar('--border', derived.borderColor);
  setVar('--input', derived.inputBorderColor);
  setVar('--ring', colors.primaryColor || colors.primaryButton);

  setVar('--destructive', derived.dangerColor);
  setVar('--destructive-foreground', '#FFFFFF');

  setVar('--success', derived.successColor);
  setVar('--success-foreground', '#FFFFFF');
  setVar('--success-bg', derived.successBgColor);
  setVar('--warning', derived.warningColor);
  setVar('--warning-foreground', '#FFFFFF');
  setVar('--warning-bg', derived.warningBgColor);
  setVar('--danger', derived.dangerColor);
  setVar('--danger-bg', derived.dangerBgColor);
  setVar('--info', derived.infoColor);
  setVar('--info-foreground', '#FFFFFF');
  setVar('--info-bg', derived.infoBgColor);

  setVar('--chart-1', derived.chart1);
  setVar('--chart-2', derived.chart2);
  setVar('--chart-3', derived.chart3);
  setVar('--chart-4', derived.chart4);
  setVar('--chart-5', derived.chart5);

  if (derived.borderRadius) setVar('--radius', derived.borderRadius);

  setVar('--info-button', colors.informationBtnBackground);
  setVar('--info-button-hover', colors.informationBtnBackgroundHover);
  setVar('--menu-toggle', colors.menuToggleBackground);
  setVar('--menu-toggle-hover', colors.menuToggleBackgroundHover);
  setVar('--hamburger-icon', colors.hamburgerMenuIcon);
  setVar('--hamburger-icon-hover', colors.hamburgerMenuIconHover);
  setVar('--navbar-user-dropdown', colors.navbarUserDropdownBg || '#F5F0E8');
};

export const applyFontsFromWhiteLabel = (fontConfig) => {
  const existing = fontConfig || {};
  const fontData = existing.font || {};

  const bodyFont = fontData.body || "'Open Sans', sans-serif";
  const headerFont = fontData.headers || "'Open Sans', sans-serif";
  const monoFont = fontData.mono || "'Courier New', monospace";

  setVar('--font-body', bodyFont);
  setVar('--font-display', headerFont);
  setVar('--font-mono', monoFont);

  const oldLink = document.getElementById(WL_FONT_LINK_ID);
  if (oldLink) oldLink.remove();

  const oldStyle = document.getElementById(WL_FONT_STYLE_ID);
  if (oldStyle) oldStyle.remove();

  if (existing.googleFontsUrl) {
    const link = document.createElement('link');
    link.id = WL_FONT_LINK_ID;
    link.rel = 'stylesheet';
    link.href = existing.googleFontsUrl;
    document.head.appendChild(link);
  }

  if (existing.customFontCss) {
    const style = document.createElement('style');
    style.id = WL_FONT_STYLE_ID;
    style.textContent = existing.customFontCss;
    document.head.appendChild(style);
  }
};

export const applyThemeFromWhiteLabel = (wlSettings) => {
  if (!wlSettings || !Object.keys(wlSettings).length) return;

  const colors = wlSettings.brandingColors;
  if (colors) applyColorsToCSSVars(colors);

  const fontConfig = Array.isArray(wlSettings.font) ? wlSettings.font[0] : wlSettings.font;
  if (fontConfig) {
    applyFontsFromWhiteLabel(fontConfig);
  } else {
    applyFontsFromWhiteLabel(wlDefaultFonts[0]);
  }
};

export const applyCYFDefaultTheme = () => {
  applyColorsToCSSVars(cyfDefaultBrandingColors);

  setVar('--font-body', cyfDefaultFonts[0].font.body);
  setVar('--font-display', cyfDefaultFonts[0].font.headers);
  setVar('--font-mono', cyfDefaultFonts[0].font.mono);
};

export const applyThemePreview = (colorOverrides = {}, fontOverrides = {}) => {
  const mergedColors = { ...cyfDefaultBrandingColors, ...colorOverrides };
  applyColorsToCSSVars(mergedColors);

  if (fontOverrides.body) setVar('--font-body', fontOverrides.body);
  if (fontOverrides.headers) setVar('--font-display', fontOverrides.headers);
  if (fontOverrides.mono) setVar('--font-mono', fontOverrides.mono);

  if (fontOverrides.googleFontsUrl) {
    applyFontsFromWhiteLabel({
      font: fontOverrides,
      googleFontsUrl: fontOverrides.googleFontsUrl,
      customFontCss: fontOverrides.customFontCss,
    });
  }
};
